// ============================================================================
// HOOK: useLegalBI.js — v1.0 — Passo 48 MC1+MC2
// Legal Business Intelligence: cruzamento Timesheet × Financial Ledger
// Art. 61.1 — Proteção de Ponto Flutuante em todos os cálculos
// Art. 48.1 — Agregação de dados paralela com onSnapshot dual
// ============================================================================

import { useEffect, useMemo, useState } from 'react';
import { subscribeFinancialLedger, subscribeTimesheetEntries } from '../services/LegalBIRepository';
import { FIREBASE_CONFIG } from '../config/Config';
import { TRANSACTION_TYPES } from '../config/FinancialSchema';

const appId = FIREBASE_CONFIG.projectId;

// ── Limiar de Rentabilidade (R$/h) ──────────────────────────────────────────
export const PROFITABILITY_THRESHOLD_HIGH   = 250;  // ≥ 250 → verde
export const PROFITABILITY_THRESHOLD_MEDIUM = 150;  // 150–249 → amarelo
                                                     // < 150  → vermelho

// ── Helpers ──────────────────────────────────────────────────────────────────

/** Converte entry do timesheet em horas decimais */
const toDecimalHours = (entry) => {
  const h = Math.max(0, Number(entry?.hours   || 0));
  const m = Math.max(0, Number(entry?.minutes || 0));
  return Math.round((h + m / 60) * 1000) / 1000;
};

/** Resolve a chave de agrupamento de uma entrada do ledger */
const ledgerGroupKey = (entry) => {
  const pLabel = String(entry?.processLabel || '').trim();
  if (pLabel) return pLabel;
  const cId = String(entry?.clientId || '').trim();
  if (cId) return `cliente:${cId}`;
  return '_sem_vinculo_';
};

/** Resolve a chave de agrupamento de uma entrada do timesheet */
const timesheetGroupKey = (entry) => {
  const pLabel = String(entry?.processLabel || '').trim();
  if (pLabel) return pLabel;
  const cName = String(entry?.clientName || '').trim();
  if (cName) return `cliente:${cName}`;
  return '_sem_vinculo_';
};

// ── calculateProcessProfitability ────────────────────────────────────────────
// MC2: Função pura de Cross-Join Timesheet × Ledger
// Art. 61.1: Math.round() rigoroso em todos os valores monetários
// Retorna array ordenado por rentabilidade/hora DESC

export function calculateProcessProfitability(ledger = [], timesheetEntries = []) {
  // ── Fase A: Agrupar receitas do ledger ──────────────────────────────────
  const revenueByKey = new Map();
  const labelByKey   = new Map();

  for (const entry of ledger) {
    if (entry.type !== TRANSACTION_TYPES.INCOME) continue;
    if (entry.isDeleted === true) continue;
    const key = ledgerGroupKey(entry);
    const prev = revenueByKey.get(key) || 0;
    // Art. 61.1: amount já normalizado em R$ com 2 casas; somar em centavos para evitar float drift
    const cents = Math.round(Number(entry.amount || 0) * 100);
    revenueByKey.set(key, prev + cents);
    if (!labelByKey.has(key)) {
      labelByKey.set(key, String(entry.processLabel || entry.description || key).trim());
    }
  }

  // ── Fase B: Agrupar horas do timesheet ──────────────────────────────────
  const hoursByKey        = new Map();
  const billableHoursByKey = new Map();

  for (const entry of timesheetEntries) {
    const key   = timesheetGroupKey(entry);
    const hours = toDecimalHours(entry);
    hoursByKey.set(key, (hoursByKey.get(key) || 0) + hours);
    if (entry.isBillable) {
      billableHoursByKey.set(key, (billableHoursByKey.get(key) || 0) + hours);
    }
    if (!labelByKey.has(key)) {
      const pLabel = String(entry?.processLabel || '').trim();
      labelByKey.set(key, pLabel || String(entry?.clientName || key).trim());
    }
  }

  // ── Fase C: Cross-Join — unir todas as chaves conhecidas ────────────────
  const allKeys = new Set([...revenueByKey.keys(), ...hoursByKey.keys()]);
  // Remover bucket sem vínculo pois não tem valor analítico
  allKeys.delete('_sem_vinculo_');

  const rows = [];

  for (const key of allKeys) {
    const revenueCents  = revenueByKey.get(key)        || 0;
    const totalHours    = Math.round((hoursByKey.get(key)        || 0) * 1000) / 1000;
    const billableHours = Math.round((billableHoursByKey.get(key) || 0) * 1000) / 1000;

    // Art. 61.1 — Proteção de ponto flutuante: arredondar centavos → R$
    const revenueReal = Math.round(revenueCents) / 100;

    // Rentabilidade/hora: evitar divisão por zero (Art. 48.2)
    const ratePerHour = totalHours > 0
      ? Math.round((revenueReal / totalHours) * 100) / 100
      : null;

    rows.push({
      key,
      label:          labelByKey.get(key) || key,
      revenueReal,    // R$ com 2 casas
      totalHours,     // horas decimais
      billableHours,  // horas cobráveis decimais
      ratePerHour,    // R$/h ou null se sem horas
    });
  }

  // ── Fase D: Ordenar por rentabilidade DESC (null vai ao fim) ─────────────
  rows.sort((a, b) => {
    if (a.ratePerHour === null && b.ratePerHour === null) {
      return b.revenueReal - a.revenueReal;
    }
    if (a.ratePerHour === null) return 1;
    if (b.ratePerHour === null) return -1;
    return b.ratePerHour - a.ratePerHour;
  });

  return rows;
}

// ── KPIs Globais ─────────────────────────────────────────────────────────────
export function calculateGlobalKPIs(profitabilityRows) {
  const rows = Array.isArray(profitabilityRows) ? profitabilityRows : [];

  const totalRevenue = Math.round(rows.reduce((s, r) => s + r.revenueReal, 0) * 100) / 100;
  const totalHours   = Math.round(rows.reduce((s, r) => s + r.totalHours,  0) * 1000) / 1000;

  const globalRatePerHour = totalHours > 0
    ? Math.round((totalRevenue / totalHours) * 100) / 100
    : null;

  const withRate = rows.filter((r) => r.ratePerHour !== null);
  const mostProfitable  = withRate.length ? withRate[0]                   : null;
  const leastProfitable = withRate.length ? withRate[withRate.length - 1] : null;

  const countAboveThreshold = rows.filter(
    (r) => r.ratePerHour !== null && r.ratePerHour >= PROFITABILITY_THRESHOLD_HIGH
  ).length;

  return {
    totalRevenue,
    totalHours,
    globalRatePerHour,
    mostProfitable,
    leastProfitable,
    countAboveThreshold,
    totalProcesses: rows.length,
  };
}

// ── useLegalBI ────────────────────────────────────────────────────────────────
// Retorna: { profitability, kpis, loading, error }
export function useLegalBI() {
  const [ledger,           setLedger]           = useState([]);
  const [timesheetEntries, setTimesheetEntries] = useState([]);
  const [loadingLedger,    setLoadingLedger]    = useState(true);
  const [loadingTS,        setLoadingTS]        = useState(true);
  const [error,            setError]            = useState('');

  // ── Stream 1: financial_ledger ───────────────────────────────────────────
  useEffect(() => {
    if (!appId) { setLoadingLedger(false); return; }
    const unsub = subscribeFinancialLedger(appId,
      (snap) => {
        setLedger(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoadingLedger(false);
      },
      (e) => {
        setError(String(e?.message || 'Erro ao carregar ledger.'));
        setLoadingLedger(false);
      }
    );
    return () => unsub();
  }, []);

  // ── Stream 2: timesheet_entries ──────────────────────────────────────────
  useEffect(() => {
    if (!appId) { setLoadingTS(false); return; }
    const unsub = subscribeTimesheetEntries(appId,
      (snap) => {
        setTimesheetEntries(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoadingTS(false);
      },
      (e) => {
        setError(String(e?.message || 'Erro ao carregar timesheet.'));
        setLoadingTS(false);
      }
    );
    return () => unsub();
  }, []);

  // ── Motor matemático (MC2) ───────────────────────────────────────────────
  const profitability = useMemo(
    () => calculateProcessProfitability(ledger, timesheetEntries),
    [ledger, timesheetEntries]
  );

  const kpis = useMemo(() => calculateGlobalKPIs(profitability), [profitability]);

  const loading = loadingLedger || loadingTS;

  return { profitability, kpis, loading, error };
}
